import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IProduct, IProductHandlingUnit } from 'src/app/features/produits/models/products.models';
import { ICatalog, IHandlingUnitType } from 'src/app/features/catalogs/models/catalogs.models';
import { INewCatalog } from '../../../new-catalog.component';
import { environment } from 'src/environments/environment';
import { IPriceBookViewDTO } from 'src/app/features/pricebook/models/pricebook.model';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';

interface ProductDetailsDialogData {
  product: IProduct;
  catalog?: INewCatalog | ICatalog | null;
  context?: 'catalog-edit' | 'catalog-view';
  canSubmit?: boolean;
}

@Component({
  selector: 'app-product-details-dialog',
  templateUrl: './product-details-dialog.component.html',
  styleUrls: ['./product-details-dialog.component.scss']
})
export class ProductDetailsDialogComponent implements OnInit, OnDestroy {
  product: IProduct;
  catalog: ICatalog | null = null;
  context: 'catalog-edit' | 'catalog-view' = 'catalog-view';
  
  baseUrl = environment.BASE_URL_API;
  isDetailsVisible = true;
  selectedProduct: IProduct | null = null;
  isDescriptionExpanded = false;
  shouldShowToggle = false;
  currentImageIndex = 0;
  allImages: string[] = [];
  displayedHandlingUnits: IHandlingUnitType[] = [];
  selectedEnabledHandlingUnits: number[] = [];
  selectedDefaultHandlingUnit: number | null = null;
  canSubmit = true;

  constructor(
    private dialogRef: MatDialogRef<ProductDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProductDetailsDialogData,
    private cdRef: ChangeDetectorRef,
    public featureFlags: FeatureFlagService
  ) {
    this.product = { ...data.product };
    this.context = data.context || 'catalog-view';
    this.canSubmit = data.canSubmit ?? true;
    
    if (data.catalog) {
      this.catalog = data.catalog as ICatalog;
    } 
  }

  // ============================================================================
  // Lifecycle Hooks
  // ============================================================================

  ngOnInit(): void {
    if (this.product) {
      this.selectedProduct = { ...this.product };
      
      const mainImage = this.selectedProduct.image ? [this.selectedProduct.image] : [];
      const extraImages = Array.isArray(this.selectedProduct.images) ? this.selectedProduct.images : [];
      this.allImages = [...mainImage, ...extraImages];
      
      this.currentImageIndex = 0;
      
      setTimeout(() => {
        const element = document.querySelector('.description-text');
        if (element) {
          this.shouldShowToggle = element.scrollHeight > element.clientHeight;
          this.cdRef.detectChanges();
        }
      }, 200);
    }

    this.loadHandlingUnitsFromCatalog();
  }

  ngOnDestroy(): void {
  }

  // ============================================================================
  // Public Methods - Dialog Actions
  // ============================================================================

  close(): void {
    this.dialogRef.close({
      enabled_handling_unit_ids: this.selectedEnabledHandlingUnits,
      default_handling_unit_id: this.selectedDefaultHandlingUnit
    });
  }

  closeDetails(): void {
    this.isDetailsVisible = false;
    this.selectedProduct = null;
    this.close();
  }

  goToProduct(): void {
    const templateId = (this.selectedProduct as any)?.product_template_id || this.selectedProduct?.id;
    if (templateId) {
      const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
      const url = `${window.location.origin}${baseHref}inventory/${templateId}`;
      window.open(url, '_blank');
    }
  }

  // ============================================================================
  // Public Methods - Image Handling
  // ============================================================================

  selectImage(index: number): void {
    this.currentImageIndex = index;
  }

  prevImage(): void {
    if (this.allImages.length > 0) {
      this.currentImageIndex =
        (this.currentImageIndex - 1 + this.allImages.length) % this.allImages.length;
    }
  }

  nextImage(): void {
    if (this.allImages.length > 0) {
      this.currentImageIndex = (this.currentImageIndex + 1) % this.allImages.length;
    }
  }

  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/no-img.svg';
  }

  // ============================================================================
  // Public Methods - Description
  // ============================================================================

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  // ============================================================================
  // Public Methods - Handling Units
  // ============================================================================

  getSelectedEnabledHandlingUnits(): IHandlingUnitType[] {
    if (!this.displayedHandlingUnits || this.displayedHandlingUnits.length === 0) {
      return [];
    }
    if (!this.selectedEnabledHandlingUnits || this.selectedEnabledHandlingUnits.length === 0) {
      return [];
    }
    
    return this.displayedHandlingUnits.filter(hu => 
      this.selectedEnabledHandlingUnits.includes(hu.id)
    ).sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
  }

  onEnabledHandlingUnitsChange(): void {
    if (!this.canSubmit) return;
    const validIds = this.displayedHandlingUnits.map(hu => hu.id);
    this.selectedEnabledHandlingUnits = this.selectedEnabledHandlingUnits.filter(id => validIds.includes(id));

    if (this.selectedDefaultHandlingUnit && 
        !this.selectedEnabledHandlingUnits.includes(this.selectedDefaultHandlingUnit)) {
      this.selectedDefaultHandlingUnit = null;
    }
    if (this.selectedEnabledHandlingUnits.length === 1) {
      this.selectedDefaultHandlingUnit = this.selectedEnabledHandlingUnits[0];
    }
  }

  getDefaultHandlingUnitDisplayLabel(): string {
    if (!this.selectedDefaultHandlingUnit) {
      return '';
    }
    
    const selectedHU = this.displayedHandlingUnits.find(
      hu => hu.id === this.selectedDefaultHandlingUnit
    );
    
    return selectedHU ? selectedHU.name : '';
  }

  shouldShowHandlingUnitsSection(): boolean {
    const hasDisplayedHUs = this.displayedHandlingUnits && this.displayedHandlingUnits.length > 0;
    
    const productHandlingUnits = this.selectedProduct?.handling_units || [];
    const hasProductHUs = productHandlingUnits.length > 0;
    
    const hasCatalogHUs = this.catalog && 
      this.catalog.enabled_handling_units && 
      this.catalog.enabled_handling_units.length > 0;
    
    const hasCatalogHUIds = this.catalog && 
      (this.catalog as any).enabled_handling_unit_ids && 
      (this.catalog as any).enabled_handling_unit_ids.length > 0;
    
    return hasDisplayedHUs || hasProductHUs || hasCatalogHUs || hasCatalogHUIds || false;
  }

  getDisplayedHandlingUnitsForSelect(): IHandlingUnitType[] {
    return this.displayedHandlingUnits;
  }

  // ============================================================================
  // Public Methods - Product Data
  // ============================================================================

  getProductGifts(): IPriceBookViewDTO[] {
    const gifts: IPriceBookViewDTO[] = [];

    const hasDeal = (deal: IPriceBookViewDTO) => gifts.some((d) => d.id === deal.id);

    const deal = this.product.best_deal as IPriceBookViewDTO[] | null;
    if (deal && deal.length > 0) {
      for (const d of deal) {
        if (!hasDeal(d)) {
          gifts.push(d);
        }
      }
    }

    const buyXGetYDeal = this.product.best_buy_x_get_y as IPriceBookViewDTO[] | null;
    if (buyXGetYDeal && buyXGetYDeal.length > 0) {
      for (const d of buyXGetYDeal) {
        if (!hasDeal(d)) {
          gifts.push(d);
        }
      }
    }
    
    return gifts;
  }

  // ============================================================================
  // Private Methods - Handling Units Initialization
  // ============================================================================

  private loadHandlingUnitsFromCatalog(): void {
    const productHandlingUnits = this.product?.handling_units || [];
    const catalogEnabledHUIds = (this.catalog as any)?.enabled_handling_unit_ids || [];
    const catalogDefaultHUId = (this.catalog as any)?.default_handling_unit_id;

    const productEnabledHUIds = (this.product as any)?.enabled_handling_unit_ids;
    const productDefaultHUId = (this.product as any)?.default_handling_unit_id || 
      productHandlingUnits.find((hu: any) => hu.default === true)?.type_id || null;

    const allProductHUs = this.normalizeHandlingUnits(productHandlingUnits);

    // Filtrer displayedHandlingUnits pour ne garder que les HU activés par le catalogue
    if (catalogEnabledHUIds.length > 0) {
      this.displayedHandlingUnits = allProductHUs.filter(hu => catalogEnabledHUIds.includes(hu.id));
    } else {
      this.displayedHandlingUnits = allProductHUs;
    }

    this.selectedEnabledHandlingUnits = this.determineInitialEnabledHandlingUnits(
      allProductHUs,
      productEnabledHUIds,
      catalogEnabledHUIds,
      productHandlingUnits
    );

    const validIds = this.displayedHandlingUnits.map(hu => hu.id);
    this.selectedEnabledHandlingUnits = this.selectedEnabledHandlingUnits.filter(id => validIds.includes(id));

    this.selectedDefaultHandlingUnit = this.determineInitialDefaultHandlingUnit(
      allProductHUs,
      productDefaultHUId,
      catalogDefaultHUId
    );
  }

  private normalizeHandlingUnits(handlingUnits: IProductHandlingUnit[]): IHandlingUnitType[] {
    return handlingUnits
      .map(hu => ({
        id: hu.type_id || hu.id || 0,
        name: hu.name || '',
        quantity: hu.quantity || 0,
        length: undefined,
        width: undefined,
        height: undefined
      }))
      .filter(hu => hu.id > 0)
      .sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
  }

  private determineInitialEnabledHandlingUnits(
    allProductHUs: IHandlingUnitType[],
    productEnabledHUIds: number[] | undefined,
    catalogEnabledHUIds: number[],
    productHandlingUnits: IProductHandlingUnit[]
  ): number[] {
    const enabledMap = new Map<number, boolean>();
    productHandlingUnits.forEach(hu => {
      const huId = hu.type_id || hu.id;
      if (huId) {
        enabledMap.set(huId, hu.enabled !== false);
      }
    });

    if (Array.isArray(productEnabledHUIds) && productEnabledHUIds.length > 0) {
      return productEnabledHUIds.filter(id => 
        allProductHUs.some(hu => hu.id === id) && enabledMap.get(id) !== false
      );
    }

    if (Array.isArray(catalogEnabledHUIds) && catalogEnabledHUIds.length > 0) {
      const intersection = allProductHUs
        .filter(hu => catalogEnabledHUIds.includes(hu.id) && enabledMap.get(hu.id) !== false)
        .map(hu => hu.id);

      return intersection;
    }
    return allProductHUs
      .filter(hu => enabledMap.get(hu.id) !== false)
      .map(hu => hu.id);
  }

  private determineInitialDefaultHandlingUnit(
    allProductHUs: IHandlingUnitType[],
    productDefaultHUId: number | undefined,
    catalogDefaultHUId: number | undefined
  ): number | null {
    if (productDefaultHUId !== undefined) {
      const productSupportsIt = allProductHUs.some(hu => hu.id === productDefaultHUId);
      const isEnabled = this.selectedEnabledHandlingUnits.includes(productDefaultHUId);
      
      if (productSupportsIt && isEnabled) {
        return productDefaultHUId;
      }
    }

    if (catalogDefaultHUId !== undefined) {
      const productSupportsIt = allProductHUs.some(hu => hu.id === catalogDefaultHUId);
      const isEnabled = this.selectedEnabledHandlingUnits.includes(catalogDefaultHUId);
      
      if (productSupportsIt && isEnabled) {
        return catalogDefaultHUId;
      }
    }

    const productDefaultFromFlag = this.product.handling_units?.find(
      hu => (hu as any).default === true
    );
    
    if (productDefaultFromFlag) {
      const defaultId = productDefaultFromFlag.type_id || productDefaultFromFlag.id;
      if (defaultId && this.selectedEnabledHandlingUnits.includes(defaultId)) {
        return defaultId;
      }
    }
    if (this.selectedEnabledHandlingUnits.length > 0) {
      return this.selectedEnabledHandlingUnits[0];
    }
    return null;
  }
}
